import Delete from "@mui/icons-material/Delete";
import ModeEdit from "@mui/icons-material/ModeEdit";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { EpisodicEventItemFragment, WasteEpisodicEventStatus } from "generated-graphql/graphql";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useTenant } from "hooks/useTenant";
import { ReactNode } from "react";

import { formatContainerDate } from "../Containers/containerDisplay";
import {
  daysUsedInWindow,
  EVENT_STATUS_LABELS,
  EVENT_TYPE_LABELS,
  EVENT_WINDOW_DAYS,
  expiredEventAlertCopy,
  finalizedEventAlertCopy,
  formatEventWindow,
  isEventWindowLapsed,
  unshippedEventWaste,
} from "./episodicDisplay";
import { EventContainersSection } from "./EventContainersSection";
import { ObligationsSection } from "./ObligationsSection";

/**
 * EXPIRED renders red (`error`): the window closed without the waste
 * departing on time, and that is an honest non-compliance signal worth
 * surfacing as one, not a neutral status like ACTIVE or FINALIZED. The
 * invariant this card still protects is narrower than "never render red" —
 * it's that the sweep never auto-detaches waste or auto-inflates generator
 * status on an EXPIRED event (see `reconcileAccumulation.ts`); the chip color
 * carries no judgment beyond what already happened. A staff-facing alert with
 * remediation guidance is a separate, later concern this chip doesn't try to
 * replace.
 */
const EVENT_STATUS_CHIP_COLOR: Record<WasteEpisodicEventStatus, "warning" | "success" | "error"> = {
  [WasteEpisodicEventStatus.Active]: "warning",
  [WasteEpisodicEventStatus.Finalized]: "success",
  [WasteEpisodicEventStatus.Expired]: "error",
};

const EVENT_BASIS_CITATION = "40 CFR 262 subpart L";

function EventFieldValue({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Grid>
  );
}

/** The days-used-of-60 progress bar, with the ship-by deadline right-aligned
 * under it. Turns red once the window has lapsed. */
function WindowProgress({ event, lapsed }: { event: EpisodicEventItemFragment; lapsed: boolean }) {
  const daysUsed = daysUsedInWindow(event.startDate);
  return (
    <Box>
      <LinearProgress
        variant="determinate"
        value={(daysUsed / EVENT_WINDOW_DAYS) * 100}
        color={lapsed ? "error" : "primary"}
        sx={{ mb: 0.5, height: 6, borderRadius: 1 }}
      />
      <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Typography variant="caption" color="text.secondary">
          {daysUsed} of {EVENT_WINDOW_DAYS} days used
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Ship &amp; remove waste by {formatContainerDate(event.accumulationDeadline)}
        </Typography>
      </Stack>
    </Box>
  );
}

/**
 * The card's status-driven alerts: the still-ACTIVE lapsed-window warning
 * (window ended, status hasn't flipped yet), the terminal EXPIRED
 * facts-and-next-steps alert, and the zero-container nudge for an ACTIVE
 * event with nothing attached, and the FINALIZED-with-waste alert (a manual
 * finalize doesn't require the waste to be gone). `lapsed` and the
 * zero-container nudge can co-occur; the terminal statuses (EXPIRED,
 * FINALIZED) can't co-occur with either or each other. The lapsed, EXPIRED,
 * and FINALIZED-with-waste alerts render as `error`: all are honest
 * non-compliance signals — the window closed (or is about to) without the
 * waste departing on time — and the sweep and the Finalize button reaching
 * the same waste-still-on-site state must alert identically (see
 * `finalizedEventAlertCopy`). The obligation rows and the zero-container
 * nudge below stay neutral `info` — an unmet filing obligation or an
 * untracked event isn't itself a non-compliance verdict the way a closed
 * window with waste on site is.
 */
function EventStatusAlerts({
  event,
  isActive,
  lapsed,
}: {
  event: EpisodicEventItemFragment;
  isActive: boolean;
  lapsed: boolean;
}) {
  const isExpired = event.status === WasteEpisodicEventStatus.Expired;
  const finalizedAlert =
    event.status === WasteEpisodicEventStatus.Finalized
      ? finalizedEventAlertCopy(unshippedEventWaste(event.containers))
      : null;
  return (
    <>
      {lapsed && (
        <Alert severity="error">
          The 60-day window has ended — ship any remaining waste and finalize the event.
        </Alert>
      )}
      {isExpired && (
        <Alert severity="error">
          {expiredEventAlertCopy(event.accumulationDeadline, unshippedEventWaste(event.containers))}
        </Alert>
      )}
      {finalizedAlert && <Alert severity="error">{finalizedAlert}</Alert>}
      {isActive && event.containers.length === 0 && (
        <Alert severity="info">
          No containers are attached to this event — waste from it isn't being tracked.
        </Alert>
      )}
    </>
  );
}

/**
 * The card's title, status/type/container-count chips, and the Edit /
 * Finalize / Reopen / Delete action row. Edit and Finalize only render for an
 * ACTIVE event (settled once the window closes); Reopen only renders for a
 * terminal (FINALIZED or EXPIRED) event — the inverse of Edit/Finalize, since
 * a reopen is exactly what un-settles one; Delete always renders, since a
 * mistaken or unwanted event is deletable regardless of status.
 */
function EventCardHeader({
  event,
  isActive,
  isTerminal,
  onEdit,
  onFinalize,
  onReopen,
  onDelete,
}: {
  event: EpisodicEventItemFragment;
  isActive: boolean;
  isTerminal: boolean;
  onEdit?: () => void;
  onFinalize?: () => void;
  onReopen?: () => void;
  onDelete?: () => void;
}) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      flexWrap="wrap"
      gap={1.5}
    >
      <Box>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          {event.reason}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip
            size="small"
            color={EVENT_STATUS_CHIP_COLOR[event.status]}
            label={EVENT_STATUS_LABELS[event.status]}
          />
          <Chip size="small" label={EVENT_TYPE_LABELS[event.type]} />
          <Chip size="small" label={`${event.containers.length} containers`} />
        </Stack>
      </Box>
      <Stack direction="row" spacing={1}>
        {isActive && (
          <>
            <Button size="small" startIcon={<ModeEdit />} onClick={onEdit} disabled={!onEdit}>
              Edit
            </Button>
            <Button size="small" onClick={onFinalize} disabled={!onFinalize}>
              Finalize event
            </Button>
          </>
        )}
        {isTerminal && (
          <Button size="small" onClick={onReopen} disabled={!onReopen}>
            Reopen event
          </Button>
        )}
        <Button
          size="small"
          color="error"
          startIcon={<Delete />}
          onClick={onDelete}
          disabled={!onDelete}
        >
          Delete event
        </Button>
      </Stack>
    </Stack>
  );
}

/**
 * Detail card for one episodic event of any status: window, removal deadline,
 * on-site cap, the EPA notice obligations, and the event's own container
 * list. ACTIVE events additionally get the days-used progress bar, the
 * lapsed-window alert, and the Edit / Finalize actions — a finalized or
 * expired event's window is settled, so those disappear rather than render
 * disabled. A terminal (FINALIZED/EXPIRED) event instead gets Reopen, which
 * hands the ACTIVE actions back once it fires — reopening is non-destructive
 * (waste can still attach, warn-but-allow), so it has no confirm dialog,
 * unlike Finalize and Delete. Delete is offered regardless of status: a
 * mistaken or unwanted event is deletable whether it's still open or already
 * terminal. The obligations section stays for every status: an unmet
 * obligation is still owed after the window ends.
 */
export function EpisodicEventCard({
  event,
  onEdit,
  onFinalize,
  onReopen,
  onDelete,
}: {
  event: EpisodicEventItemFragment;
  onEdit?: () => void;
  onFinalize?: () => void;
  onReopen?: () => void;
  onDelete?: () => void;
}) {
  const { tenantId } = useTenant();
  const { featureFlags } = useFeatureFlags();
  const stepperEnabled = featureFlags[FeatureFlagKey.WasteSubmissionStepper] === true;
  const isActive = event.status === WasteEpisodicEventStatus.Active;
  const isTerminal =
    event.status === WasteEpisodicEventStatus.Finalized ||
    event.status === WasteEpisodicEventStatus.Expired;
  const lapsed = isActive && isEventWindowLapsed(event.accumulationDeadline);

  return (
    // Matches the container detail page's SectionCard chrome: a flat white
    // tile sitting on the page's canvas background.
    <Card elevation={0}>
      <CardContent sx={{ p: 3 }}>
        {/* Roomier than the default form rhythm: these cards are the whole
            page, so the sections can afford to breathe. */}
        <Stack spacing={3.5}>
          <EventCardHeader
            event={event}
            isActive={isActive}
            isTerminal={isTerminal}
            onEdit={onEdit}
            onFinalize={onFinalize}
            onReopen={onReopen}
            onDelete={onDelete}
          />

          <Grid container spacing={2} rowSpacing={2.5}>
            <EventFieldValue label="Event window">
              {formatEventWindow(event.startDate, event.endDate)}
            </EventFieldValue>
            <EventFieldValue label="Removal deadline">
              {formatContainerDate(event.accumulationDeadline)}
            </EventFieldValue>
            {/* Only rendered while the event is ACTIVE: the exemption is live
                behavior, and a finalized or expired event has none to describe.
                The cap exemption ends at the deadline even while the status
                is still ACTIVE (the sweep hasn't run yet), so a lapsed card
                must not claim the waste is exempt. */}
            {isActive && !lapsed && (
              <EventFieldValue label="On-site cap">
                Episodic waste exempt · status unchanged
              </EventFieldValue>
            )}
            {isActive && lapsed && (
              <EventFieldValue label="On-site cap">
                {`Exemption ended ${formatContainerDate(
                  event.accumulationDeadline
                )} · remove remaining waste`}
              </EventFieldValue>
            )}
            <EventFieldValue label="Basis">{EVENT_BASIS_CITATION}</EventFieldValue>
          </Grid>

          {isActive && <WindowProgress event={event} lapsed={lapsed} />}

          <EventStatusAlerts event={event} isActive={isActive} lapsed={lapsed} />

          <ObligationsSection
            event={event}
            tenantId={tenantId ?? ""}
            stepperEnabled={stepperEnabled}
          />

          <EventContainersSection event={event} />
        </Stack>
      </CardContent>
    </Card>
  );
}
